/**
 * Account-settings side navigation.
 * Styled to match the SellerDashboard sidebar design:
 * cream background, icon + label nav links, terracotta active state.
 */
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import NamedLink from '../../../components/NamedLink/NamedLink';
import { FormattedMessage } from '../../../util/reactIntl';

import { createGlobalState } from './hookGlobalState';

import css from './LayoutSideNavigation.module.css';

const MAX_HORIZONTAL_NAV_SCREEN_WIDTH = 1023;

const initialScrollState = { scrollLeft: 0 };
const { useGlobalState } = createGlobalState(initialScrollState);

const scrollToTab = (currentPage, scrollLeft, setScrollLeft) => {
  const el = document.querySelector(`#${currentPage}Tab`);
  if (el) {
    const parent = el.parentElement;
    const parentRect = parent.getBoundingClientRect();
    const maxScrollDistance = parent.scrollWidth - parentRect.width;
    const hasParentScrolled = parent.scrollLeft > 0;
    const scrollPositionCurrent = hasParentScrolled ? parent.scrollLeft : scrollLeft;
    const tabRect = el.getBoundingClientRect();
    const diffLeftBetweenTabAndParent = tabRect.left - parentRect.left;
    const tabScrollPosition = parent.scrollLeft + diffLeftBetweenTabAndParent;
    const scrollPositionNew =
      tabScrollPosition > maxScrollDistance
        ? maxScrollDistance
        : parent.scrollLeft + diffLeftBetweenTabAndParent;
    const needsSmoothScroll = scrollPositionCurrent !== scrollPositionNew;
    if (parent.scrollTo && (!hasParentScrolled || (hasParentScrolled && needsSmoothScroll))) {
      parent.scrollTo({ left: scrollPositionCurrent });
      parent.scrollTo({ left: scrollPositionNew, behavior: 'smooth' });
    }
    setScrollLeft(scrollPositionNew);
  }
};

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconUser = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M4 20c0-4 3.6-7 8-7s8 3 8 7"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const IconMail = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="4" y="6" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M4 8l8 5 8-5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconLock = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M8 11V7a4 4 0 0 1 8 0v4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="12" cy="16" r="1.5" fill="currentColor" />
  </svg>
);

const IconWallet = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path d="M16 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="16" cy="12" r="1.5" fill="currentColor" />
  </svg>
);

const IconCard = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="6" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M3 10h18" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M7 14h4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const IconGear = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32 1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Account-settings side nav styled to match the SellerDashboard sidebar.
 *
 * @component
 * @param {Object}  props
 * @param {Object}  props.accountSettingsNavProps
 * @param {string?} props.accountSettingsNavProps.currentPage
 * @param {boolean?} props.accountSettingsNavProps.showPaymentMethods
 * @param {boolean?} props.accountSettingsNavProps.showPayoutDetails
 * @param {string?} props.ariaLabel
 * @param {'standalone'|'embedded'} [props.variant] — `embedded` nests under seller Settings
 * @param {Function?} props.onLinkClick — optional handler on each account link (e.g. close mobile drawer)
 */
const LayoutWrapperAccountSettingsSideNav = props => {
  const [mounted, setMounted] = useState(false);
  const [scrollLeft, setScrollLeft] = useGlobalState('scrollLeft');
  const { accountSettingsNavProps, ariaLabel, variant = 'standalone', onLinkClick } = props;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted) {
      const { currentPage } = accountSettingsNavProps;
      const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
      const hasHorizontalTabLayout = hasMatchMedia
        ? window.matchMedia(`(max-width: ${MAX_HORIZONTAL_NAV_SCREEN_WIDTH}px)`)?.matches
        : true;
      if (hasHorizontalTabLayout) {
        scrollToTab(currentPage, scrollLeft, setScrollLeft);
      }
    }
  }, [mounted, accountSettingsNavProps?.currentPage]);

  const { currentPage, showPaymentMethods, showPayoutDetails } = accountSettingsNavProps;

  const navItems = [
    {
      page: 'ProfileSettingsPage',
      id: 'ProfileSettingsPageTab',
      routeName: 'ProfileSettingsPage',
      labelId: 'LayoutWrapperAccountSettingsSideNav.profileTabTitle',
      Icon: IconUser,
    },
    {
      page: 'ContactDetailsPage',
      id: 'ContactDetailsPageTab',
      routeName: 'ContactDetailsPage',
      labelId: 'LayoutWrapperAccountSettingsSideNav.contactDetailsTabTitle',
      Icon: IconMail,
    },
    {
      page: 'PasswordChangePage',
      id: 'PasswordChangePageTab',
      routeName: 'PasswordChangePage',
      labelId: 'LayoutWrapperAccountSettingsSideNav.passwordTabTitle',
      Icon: IconLock,
    },
    ...(showPayoutDetails
      ? [
          {
            page: 'StripePayoutPage',
            id: 'StripePayoutPageTab',
            routeName: 'StripePayoutPage',
            labelId: 'LayoutWrapperAccountSettingsSideNav.paymentsTabTitle',
            Icon: IconWallet,
          },
        ]
      : []),
    ...(showPaymentMethods
      ? [
          {
            page: 'PaymentMethodsPage',
            id: 'PaymentMethodsPageTab',
            routeName: 'PaymentMethodsPage',
            labelId: 'LayoutWrapperAccountSettingsSideNav.paymentMethodsTabTitle',
            Icon: IconCard,
          },
        ]
      : []),
    {
      page: 'ManageAccountPage',
      id: 'ManageAccountPageTab',
      routeName: 'ManageAccountPage',
      labelId: 'LayoutWrapperAccountSettingsSideNav.manageAccountTabTitle',
      Icon: IconGear,
    },
  ];

  return (
    <nav
      className={classNames(css.settingsNav, {
        [css.settingsNavEmbedded]: variant === 'embedded',
      })}
      aria-label={ariaLabel}
    >
      {variant === 'standalone' ? (
        <p className={css.settingsNavHeading}>
          <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.heading" />
        </p>
      ) : null}
      {navItems.map(({ page, id, routeName, labelId, Icon }) => {
        const isActive = currentPage === page;
        return (
          <NamedLink
            key={id}
            id={id}
            name={routeName}
            onClick={onLinkClick}
            className={
              isActive
                ? `${css.settingsNavLink} ${css.settingsNavLinkActive}`
                : css.settingsNavLink
            }
          >
            <span className={css.settingsNavIcon}>
              <Icon />
            </span>
            <FormattedMessage id={labelId} />
          </NamedLink>
        );
      })}
    </nav>
  );
};

export default LayoutWrapperAccountSettingsSideNav;
